'use client';

import './footer.scss';

import { useTranslation } from 'react-i18next';

import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { LogoName } from '@sola/schema/lib/logos.ts';
import type { FooterProps } from '@sola/schema/lib/skippy.ts';
import { submitSubscription } from '@sola/skippy-sdk/requests.ts';
import { openPreferenceCenter } from '@sola/tracking/lib/open-preference-center.ts';

import { FormEmailSubscribe } from '../forms/form-email-subscribe/form-email-subscribe.tsx';

type CustomizeProps = {
  logo?: LogoName;
  logoStyles?: string;
  experiments?: string[];
  showSubscribeForm?: boolean;
};

const FooterDivider: React.FC = () => (
  <Divider purpose="neutral" className="FooterSkippy__divider my-roomy-xs" />
);

export const Footer: React.FC<FooterProps & CustomizeProps> = ({
  footerMenuPrimary,
  footerMenuSecondary,
  footerMenuSocial,
  footerMenuTrust,
  logo = 'skippy',
  logoStyles,
  showSubscribeForm = true,
}) => {
  const { t } = useTranslation(['forms', 'footer']);
  const trustItemsWithLogo = footerMenuTrust?.items?.filter(
    (ti) => ti.icon,
  ) ?? [
    {
      link: 'https://app.candid.org/profile/7454344/compassion-international-inc-36-2423707',
      title: t('footer:logoAlts.candid'),
      icon: 'trust-guide-star',
    },
    {
      link: 'https://give.org/charity-reviews/child-sponsorship/compassion-international-in-colorado-springs-co-3017',
      title: t('footer:logoAlts.bbb'),
      icon: 'trust-bbb',
    },
    {
      link: 'https://www.ecfa.org/MemberProfile.aspx?ID=4466',
      title: t('footer:logoAlts.ecfa'),
      icon: 'trust-ecfa',
    },
    {
      link: 'https://www.charitywatch.org/charities/compassion-international',
      title: t('footer:logoAlts.charityWatch'),
      icon: 'trust-charity-watch',
    },
    {
      link: 'https://www.guidestar.org/profile/36-2423707',
      title: t('footer:logoAlts.guidestar'),
      icon: 'trust-seal-of-transparency-guide-star',
    },
  ];
  const showTrustedLogos = trustItemsWithLogo.length > 0;
  return (
    <footer className="FooterSkippy component">
      <div className="container flex flex-col">
        {(showSubscribeForm || showTrustedLogos) && (
          <>
            <div className="FooterSkippy__container flex flex-col xl:flex-row xl:items-end justify-between gap-roomy-xs">
              {showSubscribeForm && (
                <div className="FooterSkippy__EmailSkeleton w-full">
                  <FormEmailSubscribe
                    className="max-w-md"
                    subscriptionCode="Leads_General_Sign_Up"
                    title={t('forms:emailSubscribe.title')}
                    description={t('forms:emailSubscribe.description')}
                    onSubmit={submitSubscription}
                  />
                </div>
              )}
              {showTrustedLogos && (
                <div className="flex flex-col xl:flex-row gap-roomy-xs justify-end">
                  <div className="flex gap-comfy-md items-center">
                    {trustItemsWithLogo.map((ti) => (
                      <Link
                        href={ti.link || '#'}
                        target="_blank"
                        rel="noreferrer"
                        title={ti.title as string}
                        key={ti.icon}
                      >
                        <LogoChooser
                          alt={ti.title as string}
                          key={ti.icon}
                          role="img"
                          name={ti.icon as LogoName}
                          className="FooterSkippy__trust-icon"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <FooterDivider />
          </>
        )}
        {footerMenuPrimary?.items && footerMenuPrimary.items.length > 0 && (
          <div className="FooterSkippy__container grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-comfy-xl">
            {footerMenuPrimary?.items?.map((i) => (
              <div className="flex flex-col gap-comfy-sm" key={i.title}>
                {i.link ? (
                  <Link
                    href={i.link}
                    className="FooterSkippy__link label-md label-potent"
                  >
                    {i.title}
                  </Link>
                ) : (
                  <span className="label-md label-potent">{i.title}</span>
                )}
                {i.items?.map((c, index) =>
                  c.link ? (
                    <Link
                      href={c.link}
                      className="FooterSkippy__link label-md"
                      key={c.link}
                    >
                      {c.title}
                    </Link>
                  ) : (
                    <span key={c.title ?? index} className="label-md">
                      {c.title}
                    </span>
                  ),
                )}
              </div>
            ))}
          </div>
        )}
        <FooterDivider />
        <div className="flex gap-roomy-xs md:flex-row flex-col md:items-start md:justify-between">
          <div className="flex flex-col gap-comfy-sm max-w-xl">
            <LogoChooser
              className={logoStyles}
              name={logo}
              color="white"
              aria-hidden
            />
            <p className="label-md m-0">{t('footer:copyright')} </p>
            <ul className="FooterSkippy__terms-links flex flex-wrap gap-comfy-xs m-none p-none">
              {footerMenuSecondary?.items?.map((si) => (
                <li key={si.title}>
                  {si.link ? (
                    <Link
                      href={si.link}
                      className="FooterSkippy__link label-md"
                    >
                      {si.title!}
                    </Link>
                  ) : (
                    <span className="label-md">{si.title!}</span>
                  )}
                </li>
              ))}
              <li className="cookie-preferences">
                <button
                  type="button"
                  className="FooterSkippy__link label-md"
                  onClick={() => {
                    openPreferenceCenter();
                  }}
                >
                  {t('footer:cookiePreferences')}
                </button>
              </li>
            </ul>
            <span className="flex flex-col">
              <p className="label-md">{t('footer:nonProfit')}</p>
              <p className="label-md">{t('footer:taxNumber')}</p>
            </span>
            <p className="label-md">{t('footer:trademarks')}</p>
          </div>
          {!!footerMenuSocial?.items?.length && (
            <div className="flex gap-comfy-md items-center">
              {footerMenuSocial?.items
                ?.filter((si) => si.icon)
                .map((si) => (
                  <ArkIcon
                    iconName={si.icon!}
                    key={si.title}
                    className="FooterSkippy__social-icon"
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
